/**
 * Error Boundary Component
 * 
 * Catches React component errors and displays a fallback UI.
 * Logs errors for debugging and monitoring.
 */

import React, { Component, ReactNode, ErrorInfo } from 'react';
import * as Sentry from '@sentry/react';
import { logger } from '@/lib/logging';
import { handleError } from '@/lib/errors';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage: string;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      errorMessage: '',
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      errorMessage: error.message,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const result = handleError(error, 'ErrorBoundary');
    
    // Send to Sentry with additional context (only if Sentry is initialized)
    const isSentryInitialized = !!(import.meta?.env?.VITE_SENTRY_DSN || import.meta?.env?.SENTRY_DSN);
    
    if (isSentryInitialized) {
      Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
        tags: {
          errorBoundary: true,
          errorCode: result.code,
        },
        level: 'error',
      });
    }
    
    logger.error('React Error Boundary caught an error', {
      error: result.technicalMessage,
      code: result.code,
      componentStack: errorInfo.componentStack,
    });
  }

  private handleReset = (): void => {
    this.setState({
      hasError: false,
      errorMessage: '',
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-dark-950 text-white p-6">
          <div className="max-w-md text-center space-y-6">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
              <svg 
                className="w-10 h-10 text-red-500" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                />
              </svg>
            </div>
            
            <div>
              <h1 className="text-2xl font-bold mb-2">Something Went Wrong</h1>
              <p className="text-gray-400 mb-4">
                An unexpected error occurred. The error has been logged and we will investigate.
              </p>
              {this.state.errorMessage && (
                <p className="text-sm text-gray-500 bg-dark-900 p-3 rounded-lg border border-white/5 font-mono">
                  {this.state.errorMessage}
                </p>
              )}
            </div>

            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleReset}
                className="px-6 py-2 bg-brand-500 hover:bg-brand-600 rounded-lg font-medium transition-colors"
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="px-6 py-2 bg-dark-800 hover:bg-dark-700 border border-white/10 rounded-lg font-medium transition-colors"
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
